import { Input } from '@payaca/ambrosia';
import { FC, useCallback, useEffect, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import { useSelector } from '@/api/state';

import LabelValuePair from '@payaca/components/labelValuePair/LabelValuePair';

import { currencyPrice } from '@payaca/helpers/financeHelper';

import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';

import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { InvoicesPermissions } from '@payaca/permissions/invoices/invoices.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import * as dealActions from '@payaca/store/deals/dealsActions';
import {
  useDeal,
  useJobContent,
  useProposal,
} from '@payaca/store/hooks/appState';
import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';
import * as jobActions from '@payaca/store/jobs/jobsActions';
import { Job } from '@payaca/types/jobTypesV2';
import UntitledIcon from '@payaca/untitled-icons';
import { getUserRoles } from '../../../utils/stateAccessors';
import DealPaymentOptionsControl from '../dealPaymentOptionsControl/DealPaymentOptionsControl';
import DealPriceDetailControl from '../dealPriceDetailControl/DealPriceDetailControl';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import { ProjectAccountingIntegrationCategoriesControl } from '../projectAccountingIntegrationCategoriesControl/ProjectAccountingIntegrationCategoriesControl';
import SetProposalDiscountModal from '../setProposalDiscountModal/SetProposalDiscountModal';
import './ProtoInvoiceSidePanel.sass';

type Props = {
  protoInvoiceId: number;
};

const ProtoInvoiceSidePanel: FC<Props> = ({
  protoInvoiceId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const [showSetDiscountModal, setShowSetDiscountModal] = useState(false);
  const userRoles = useSelector(getUserRoles);

  const protoInvoice: Job | undefined = useProposal(protoInvoiceId);
  const jobContent = useJobContent(protoInvoice?.jobContentId);

  const region = useSelector(
    (state: any) => state.users.myProfile.accounts[0].region
  );

  const deal = useDeal(protoInvoice?.dealId);

  const [isSaveRequired, setIsSaveRequired] = useState(false);

  const isUpdatingProtoInvoice: boolean = useSelector((state) => {
    return state.jobsStore.isEditingJob;
  });

  const isFetchingProtoInvoice: boolean = useSelector((state) => {
    return (
      state.jobsStore.jobs && state.jobsStore.jobs[protoInvoiceId]?.isFetching
    );
  });

  const onUpdateSuccess = useCallback(() => {
    dispatch(jobActions.requestGetJob(protoInvoiceId));
  }, [dispatch, protoInvoiceId]);

  const [dueInDays, setDueInDays] = useState<number | null>();

  useEffect(() => {
    setDueInDays(protoInvoice?.invoiceConfig?.dueInDays);
  }, [protoInvoice?.invoiceConfig?.dueInDays]);

  const persistDueInDays = useCallback(() => {
    dispatch(
      jobActions.partialUpdateJob.request({
        jobId: protoInvoiceId,
        data: {
          invoiceConfig: {
            ...protoInvoice?.invoiceConfig,
            dueInDays: dueInDays,
          },
        },
        callback: onUpdateSuccess,
        onErrorCallback: () => {
          console.log();
        },
      })
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'onUpdateSuccess'
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: isUpdatingProtoInvoice
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: isFetchingProtoInvoice
  }, [
    dispatch,
    dueInDays,
    protoInvoice,
    protoInvoiceId,
    isUpdatingProtoInvoice,
    isFetchingProtoInvoice,
  ]);

  return (
    <div className="proto-invoice-side-panel mb-8">
      <div className="invoice-due-days-wrapper">
        Invoice due in{' '}
        <Input
          className="w-[70px] inline-block align-middle"
          sizeVariant="sm"
          name="dueInDays"
          value={dueInDays || ''}
          type="number"
          onChange={(value) => {
            if (!isNaN(Number(value)) && value !== '') {
              setDueInDays(Math.round(Number(value)));
            } else {
              setDueInDays(undefined);
            }
          }}
          changeTimeoutMs={2000}
          onBlur={() => {
            persistDueInDays();
          }}
          onChangeTimeout={() => setIsSaveRequired(true)}
          disabled={
            !userHasRequiredPermission(userRoles, [
              InvoicesPermissions.EDIT_INVOICE_DUE_IN_DAYS,
            ])
          }
        />{' '}
        days
      </div>
      {!!jobContent && (
        <div className="invoice-totals-container">
          <LabelValuePair
            suffixLabelWith=""
            label="Subtotal"
            value={currencyPrice(jobContent.subtotal, region)}
          />
          {!!jobContent.calculatedMarkupAmount && (
            <LabelValuePair
              suffixLabelWith=""
              label="Markup"
              value={currencyPrice(jobContent.calculatedMarkupAmount, region)}
            />
          )}
          {!jobContent.calculatedDiscountAmount ? (
            <>
              <Button
                styleVariant={ButtonStyleVariant.ANCHOR}
                onClick={() => setShowSetDiscountModal(true)}
              >
                Set discount
              </Button>
            </>
          ) : (
            <>
              <LabelValuePair
                suffixLabelWith=""
                label={
                  <>
                    <span>Discount</span>
                    <Button
                      styleVariant={ButtonStyleVariant.ANCHOR}
                      onClick={() => setShowSetDiscountModal(true)}
                    >
                      <UntitledIcon name="edit-02.3" />
                    </Button>
                  </>
                }
                value={
                  jobContent.calculatedDiscountAmount
                    ? currencyPrice(jobContent.calculatedDiscountAmount, region)
                    : undefined
                }
              />
            </>
          )}

          <LabelValuePair
            suffixLabelWith=""
            label="Tax"
            value={currencyPrice(jobContent.vatTotal, region)}
          />

          {!!jobContent.cisTotal && (
            <LabelValuePair
              suffixLabelWith=""
              label="CIS suffered"
              value={`${
                jobContent.cisTotal * -1 >= 0 ? '+' : ''
              }${currencyPrice(jobContent.cisTotal * -1, region)}`}
            />
          )}
          <hr />
          <LabelValuePair
            suffixLabelWith=""
            label="Total"
            value={currencyPrice(jobContent.total, region)}
            className="total"
          />
        </div>
      )}
      {protoInvoice && (
        <>
          <PermissionGuard
            renderIfHasPermissions={[DealsPermissions.MANAGE_PAYMENT_OPTIONS]}
          >
            <DealPaymentOptionsControl dealId={protoInvoice?.dealId} />
          </PermissionGuard>
          <PermissionGuard
            renderIfHasPermissions={[DealsPermissions.MANAGE_PRICE_VISIBILITY]}
          >
            <DealPriceDetailControl
              dealId={protoInvoice.dealId}
              canHideVat={false}
            />
          </PermissionGuard>
          {deal && (
            <ProjectAccountingIntegrationCategoriesControl
              projectId={deal.publicId}
            />
          )}
        </>
      )}
      <SetProposalDiscountModal
        proposalId={protoInvoiceId}
        isOpen={showSetDiscountModal}
        onClose={() => setShowSetDiscountModal(false)}
        onSetProposalDiscountSuccess={() => {
          if (protoInvoice) {
            dispatch(
              jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
                protoInvoice.jobContentId
              )
            );
            dispatch(jobActions.requestGetJob(protoInvoice.id));
            dispatch(dealActions.requestGetDeal(protoInvoice.dealId));
          }
        }}
      />
    </div>
  );
};
export default ProtoInvoiceSidePanel;
